<template>
	<div class="page-container">
		<!-- Top User Banner -->
		<div class="user-banner">
			<div class="user-info-wrapper">
				<div class="avatar-section">
					<div class="avatar-circle">
						<img :src="userInfo.avatarImg || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix'"
							alt="User Avatar" />
					</div>
					<div class="user-details">
						<div class="name-row">
							<span class="username">{{userInfo.nickname || '用户'}}</span>
							<span class="vip-badge" v-if="userInfo.isMember === 1">
								<img src="../assets/icon/vipIcon.png" alt="" style="width: 20px; height: 20px;">
							</span>
						</div>
						<div class="user-id">ID: {{userInfo.userId || '--'}}</div>
					</div>
				</div>
				<div class="message-icon" @click="openNotificationPopup">
					<img src="../assets/icon/messageIcon.png" alt="" />
				</div>
			</div>
		</div>

		<!-- Main Content Area -->
		<div class="main-content">
			<!-- Left Sidebar -->
			<div class="sidebar">
				<ul class="menu-list">
					<li v-for="(item, index) in menuItems" :key="index" class="menu-item"
						:class="{ active: currentMenu === item.id }" @click="currentMenu = item.id">
						<span>{{ item.label }}</span>
						<span class="arrow">&gt;</span>
					</li>
				</ul>
			</div>

			<!-- Right Content -->
			<div class="content-area">
				<component :is="currentComponent" v-if="currentComponent" />

				<!-- Placeholder for other menus -->
				<div class="content-card" v-if="!currentComponent">
					<div class="card-header">
						<h2>{{ currentMenuLabel }}</h2>
					</div>
					<div class="empty-state">
						功能开发中...
					</div>
				</div>
			</div>
		</div>

		<!-- 消息通知列表弹窗 -->
		<Teleport to="body">
			<Transition name="notification-fade">
				<div v-if="showNotificationPopup" class="notification-overlay" @click.self="closeNotificationPopup">
					<div class="notification-popup">
						<div class="notification-header">
							<h3>消息通知</h3>
							<span class="notification-close" @click="closeNotificationPopup">&times;</span>
						</div>
						<div class="notification-body">
							<!-- 消息详情 -->
							<template v-if="notificationDetail !== null">
								<div class="notification-detail">
									<button class="notification-back" @click="backToNotificationList">← 返回列表</button>
									<div v-if="notificationDetailLoading" class="notification-loading">加载中...</div>
									<template v-else>
										<h4 class="notification-detail-title">{{ notificationDetail.title || '系统通知' }}
										</h4>
										<div class="notification-detail-time">{{ notificationDetail.createTime  }}</div>
										<div class="notification-detail-content">
											{{ notificationDetail.content || '暂无内容' }}
										</div>
									</template>
								</div>
							</template>
							<!-- 消息列表 -->
							<template v-else>
								<div v-if="notificationLoading" class="notification-loading">加载中...</div>
								<div v-else-if="!notificationList.length" class="notification-empty">暂无消息</div>
								<ul v-else class="notification-list">
									<li v-for="(item, index) in notificationList" :key="item.id || index"
										class="notification-item" @click="viewNotificationDetail(item)">
										<div class="notification-item-title">{{ item.title || item.content || '系统通知' }}
										</div>
										<div class="notification-item-content" v-if="item.content && item.title">
											{{ item.content }}
										</div>
										<div class="notification-item-time">
											{{ item.createTime  }}
										</div>
									</li>
								</ul>
							</template>
						</div>
					</div>
				</div>
			</Transition>
		</Teleport>
	</div>
</template>

<script>
	import {
		getUserProfile
	} from '@/api/user'
	import {
		pageBusNotification,
		getBusNotificationInfo
	} from '@/api/public'
	import UserProfile from './personal/UserProfile.vue'
	import MyWallet from './personal/MyWallet.vue'
	import MyCourses from './personal/MyCourses.vue'
	import MyFavorites from './personal/MyFavorites.vue'
	import PromotionCenter from './personal/PromotionCenter.vue'
	import ContactService from './personal/ContactService.vue'
	import UserSettings from './personal/UserSettings.vue'

	export default {
		name: 'PersonalCenter',
		components: {
			UserProfile,
			MyWallet,
			MyCourses,
			MyFavorites,
			PromotionCenter,
			ContactService,
			UserSettings
		},
		watch: {
			'$route.query.tab': {
				immediate: true,
				handler(val) {
					if (val && this.menuItems.some(item => item.id === val)) {
						this.currentMenu = val
					}
				}
			}
		},
		created() {
			this.getUserInfo()
		},
		data() {
			return {
				userInfo: {},
				currentMenu: 'profile',
				showNotificationPopup: false,
				notificationList: [],
				notificationLoading: false,
				notificationDetail: null,
				notificationDetailLoading: false,
				menuItems: [{
						id: 'profile',
						label: '个人资料'
					},
					{
						id: 'wallet',
						label: '我的钱包'
					},
					{
						id: 'courses',
						label: '我的课程'
					},
					{
						id: 'favorites',
						label: '我的收藏',
						icon: ''
					},
					{
						id: 'promotion',
						label: '分销推广',
						icon: ''
					},
					{
						id: 'contact',
						label: '联系客服'
					},
					{
						id: 'settings',
						label: '设置'
					}
				]
			}
		},
		methods: {
			async getUserInfo() {
				try {
					const res = await getUserProfile()
					this.userInfo = res
				} catch (e) {
					console.log(e)
				}
			},
			async openNotificationPopup() {
				this.showNotificationPopup = true
				this.notificationLoading = true
				this.notificationList = []
				try {
					const res = await pageBusNotification()
					// 兼容 list / records / 直接数组
					const list = res.rows
					this.notificationList = list
				} catch (e) {
					console.log(e)
				} finally {
					this.notificationLoading = false
				}
			},
			closeNotificationPopup() {
				this.showNotificationPopup = false
				this.notificationDetail = null
			},
			async viewNotificationDetail(item) {
				const id = item.notificationId
				if (!id) return
				this.notificationDetail = {
					title: item.title || item.content
				}
				this.notificationDetailLoading = true
				try {
					const res = await getBusNotificationInfo(id)
					this.notificationDetail = res
				} catch (e) {
					console.log(e)
					this.notificationDetail = null
				} finally {
					this.notificationDetailLoading = false
				}
			},
			backToNotificationList() {
				this.notificationDetail = null
			},
			formatNotificationTime(time) {
				if (!time) return ''
				const date = new Date(time)
				const now = new Date()
				const diff = now - date
				if (diff < 60000) return '刚刚'
				if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
				if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
				if (diff < 604800000) return Math.floor(diff / 86400000) + '天前'
				return date.toLocaleDateString()
			}
		},
		computed: {
			currentComponent() {
				const map = {
					profile: 'UserProfile',
					wallet: 'MyWallet',
					courses: 'MyCourses',
					favorites: 'MyFavorites',
					promotion: 'PromotionCenter',
					contact: 'ContactService',
					settings: 'UserSettings'
				}
				return map[this.currentMenu] || null
			},
			currentMenuLabel() {
				const item = this.menuItems.find(i => i.id === this.currentMenu)
				return item ? item.label : ''
			}
		}
	}
</script>

<style scoped lang="scss">
	.page-container {
		min-height: 100vh;
		background-color: #121212;
		color: #fff;
		padding-bottom: 40px;
		width: 100%;
	}

	/* Banner Styles */
	.user-banner {
		width: 100vw;
		position: relative;
		left: 50%;
		transform: translateX(-50%);
		height: 160px;
		background-image: url('@/assets/image/personalBackground.png');
		background-size: cover;
		background-repeat: no-repeat;
		background-position: center;
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 30px;
		border-bottom: 1px solid #333;

		.user-info-wrapper {
			width: 1200px;
			padding: 0 20px;
			display: flex;
			justify-content: space-between;
			align-items: center;

			.avatar-section {
				display: flex;
				align-items: center;
				gap: 20px;

				.avatar-circle {
					width: 80px;
					height: 80px;
					border-radius: 50%;
					background-color: #fff;
					overflow: hidden;
					border: 2px solid #ffd700;

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}
				}

				.user-details {
					display: flex;
					flex-direction: column;
					gap: 5px;

					.name-row {
						display: flex;
						align-items: center;
						gap: 10px;

						.username {
							font-size: 20px;
							font-weight: bold;
							color: #fff;
						}

						.vip-badge {
							font-size: 16px;
						}
					}

					.user-id {
						font-size: 14px;
						color: #999;
					}
				}
			}

			.message-icon {
				cursor: pointer;

				img {
					width: 26px;
					height: 26px;
				}
			}
		}
	}

	/* Main Content Layout */
	.main-content {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
		display: flex;
		gap: 20px;

		/* Sidebar Styles */
		.sidebar {
			width: 240px;
			flex-shrink: 0;
			background-color: #1e1e1e;
			border-radius: 4px;
			padding: 10px 0;
			height: fit-content;

			.menu-list {
				list-style: none;
				padding: 0;
				margin: 0;

				.menu-item {
					padding: 15px 25px;
					display: flex;
					justify-content: space-between;
					align-items: center;
					color: #999;
					cursor: pointer;
					transition: all 0.3s;
					font-size: 14px;

					&:hover {
						background-color: #2a2a2a;
						color: #fff;
					}

					&.active {
						color: #ff9900;
						background-color: #2a2a2a;
					}

					.arrow {
						font-family: consolas;
					}
				}
			}
		}

		/* Content Area Styles */
		.content-area {
			flex: 1;

			/* Placeholder Styles */
			.content-card {
				background-color: #1e1e1e;
				border-radius: 4px;
				min-height: 600px;
				padding: 20px 10px;
				border-bottom: 1px solid #333;

				.card-header {
					margin-bottom: 40px;

					h2 {
						font-size: 18px;
						font-weight: normal;
						color: #ddd;
						padding-bottom: 15px;
						display: inline-block;
						border-bottom: 2px solid #ff9900;
						margin: 0;
					}
				}

				.empty-state {
					color: #666;
					text-align: center;
					margin-top: 100px;
				}
			}
		}
	}

	/* 消息通知弹窗 */
	.notification-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.6);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
	}

	.notification-popup {
		width: 480px;
		max-width: 90vw;
		max-height: 70vh;
		background: #1e1e1e;
		border-radius: 8px;
		border: 1px solid #333;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.notification-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid #333;

		h3 {
			margin: 0;
			font-size: 18px;
			font-weight: 600;
			color: #fff;
		}

		.notification-close {
			font-size: 24px;
			color: #999;
			cursor: pointer;
			line-height: 1;

			&:hover {
				color: #fff;
			}
		}
	}

	.notification-body {
		padding: 16px;
		overflow-y: auto;
		flex: 1;
		min-height: 200px;
	}

	.notification-loading,
	.notification-empty {
		text-align: center;
		color: #666;
		padding: 40px 0;
	}

	.notification-list {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.notification-item {
		padding: 14px 0;
		border-bottom: 1px solid #2a2a2a;
		cursor: pointer;

		&:last-child {
			border-bottom: none;
		}

		.notification-item-title {
			color: #fff;
			font-size: 15px;
			margin-bottom: 6px;
		}

		.notification-item-content {
			color: #999;
			font-size: 13px;
			line-height: 1.5;
			margin-bottom: 6px;
		}

		.notification-item-time {
			color: #666;
			font-size: 12px;
		}
	}

	.notification-detail {
		.notification-back {
			background: none;
			border: none;
			color: #ff9900;
			cursor: pointer;
			font-size: 14px;
			margin-bottom: 16px;
			padding: 0;

			&:hover {
				text-decoration: underline;
			}
		}

		.notification-detail-title {
			margin: 0 0 8px;
			font-size: 16px;
			color: #fff;
			font-weight: 600;
		}

		.notification-detail-time {
			color: #666;
			font-size: 12px;
			margin-bottom: 16px;
		}

		.notification-detail-content {
			color: #ccc;
			font-size: 14px;
			line-height: 1.6;
			white-space: pre-wrap;
			word-break: break-word;
		}
	}

	.notification-fade-enter-active,
	.notification-fade-leave-active {
		transition: opacity 0.2s ease;

		.notification-popup {
			transition: transform 0.2s ease;
		}
	}

	.notification-fade-enter-from,
	.notification-fade-leave-to {
		opacity: 0;

		.notification-popup {
			transform: scale(0.95);
		}
	}
</style>